/**
 * CompanyListModal 컴포넌트 - 세금계산서 발급 시 회사 선택 모달
 * (company-list/CompanyListModal.tsx에서 통합됨)
 */

import { useLanguage } from '@/contexts/AppContext';
import type { CompanyListModalProps } from './types';
import { companyListTranslations } from './company-list-constants';
import { useCompanyList } from './useCompanyList';
import { CompanyListHeader } from './CompanyListHeader';
import { CompanyQuickForm } from './CompanyQuickForm';
import { CompanySelectCard, AddCompanyButton } from './CompanySelectCard';

export function CompanyListModal({
  isOpen,
  onClose,
  onSelectCompany,
}: CompanyListModalProps) {
  // #522: 15 locale 전수 지원 — locale 로 lookup
  const { locale } = useLanguage();
  const t = companyListTranslations[locale];

  const {
    showAddForm,
    companies,
    formData,
    errors,
    handleSaveCompany,
    handleFormChange,
    handleShowAddForm,
    handleHideAddForm
  } = useCompanyList(t);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md mx-auto shadow-2xl max-h-[90vh] overflow-hidden">
        <CompanyListHeader
          showAddForm={showAddForm}
          t={t}
          onClose={onClose}
          onBack={handleHideAddForm}
        />

        <div className="max-h-[calc(90vh-120px)] overflow-y-auto">
          {showAddForm ? (
            <CompanyQuickForm
              formData={formData}
              errors={errors}
              t={t}
              onFormChange={handleFormChange}
              onSave={handleSaveCompany}
              onCancel={handleHideAddForm}
            />
          ) : (
            <div className="p-6">
              <div className="space-y-3 mb-6">
                {companies.map((company) => (
                  <CompanySelectCard
                    key={company.id}
                    company={company}
                    t={t}
                    onSelect={onSelectCompany}
                  />
                ))}
              </div>

              <AddCompanyButton
                label={t.addNewCompany}
                onClick={handleShowAddForm}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
